import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getAllBlogs, getBlogBySlug as fetchBlogBySlug, createBlog as addNewBlog, updateBlog as updateExistingBlog, deleteBlog as removeExistingBlog } from '../services/blogService';
import { useAuth } from './AuthContext';

export interface BlogPost {
  id: string;
  title: string;
  slug: string;
  date: string;
  author: string;
  excerpt: string;
  content: string;
  image: string;
  categories: string[];
  tags: string[];
  readTime: number;
}

interface BlogContextType {
  posts: BlogPost[];
  loading: boolean;
  error: string | null;
  addPost: (post: Omit<BlogPost, 'id'>) => Promise<BlogPost>;
  updatePost: (id: string, post: Partial<BlogPost>) => Promise<BlogPost>;
  deletePost: (id: string) => Promise<void>;
  getPostBySlug: (slug: string) => Promise<BlogPost | undefined>;
}

const BlogContext = createContext<BlogContextType | undefined>(undefined);

const getPostTime = (post: BlogPost) => {
  const time = new Date(post.date).getTime();
  return Number.isNaN(time) ? 0 : time;
};

const sortPostsByNewestFirst = (items: BlogPost[]) =>
  [...items].sort((a, b) => getPostTime(b) - getPostTime(a));

export const useBlog = () => {
  const context = useContext(BlogContext);
  if (context === undefined) {
    throw new Error('useBlog must be used within a BlogProvider');
  }
  return context;
};

interface BlogProviderProps {
  children: ReactNode;
}

export const BlogProvider = ({ children }: BlogProviderProps) => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch blogs on component mount
  useEffect(() => {
    const fetchBlogs = async () => {
      if (authLoading) return;

      try {
        setLoading(true);
        const blogs = await getAllBlogs(isAuthenticated);
        setPosts(sortPostsByNewestFirst(blogs));
        setError(null);
      } catch (err) {
        console.error('Error fetching blogs:', err);
        setError('Failed to load blogs');
      } finally {
        setLoading(false);
      }
    };

    fetchBlogs();
  }, [authLoading, isAuthenticated]);

  const addPost = async (post: Omit<BlogPost, 'id'>) => {
    try {
      setLoading(true);
      const newPost = await addNewBlog(post);
      setPosts(sortPostsByNewestFirst([newPost, ...posts]));
      return newPost;
    } catch (err) {
      console.error('Error adding blog post:', err);
      setError('Failed to add blog post');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updatePost = async (id: string, post: Partial<BlogPost>) => {
    try {
      setLoading(true);
      const updatedPost = await updateExistingBlog(id, post);
      setPosts(sortPostsByNewestFirst(
        posts.map((p) => (p.id === id ? updatedPost : p))
      ));
      return updatedPost;
    } catch (err) {
      console.error('Error updating blog post:', err);
      setError('Failed to update blog post');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deletePost = async (id: string) => {
    try {
      setLoading(true);
      await removeExistingBlog(id);
      setPosts(posts.filter((p) => p.id !== id));
    } catch (err) {
      console.error('Error deleting blog post:', err);
      setError('Failed to delete blog post');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const getPostBySlug = async (slug: string) => {
    // Check if post exists in state first
    const existingPost = posts.find((p) => p.slug === slug);
    if (existingPost) return existingPost;

    try {
      setLoading(true);
      const post = await fetchBlogBySlug(slug);
      return post;
    } catch (err) {
      console.error(`Error fetching blog post with slug ${slug}:`, err);
      setError('Failed to load blog post');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return (
    <BlogContext.Provider
      value={{
        posts,
        loading,
        error,
        addPost,
        updatePost,
        deletePost,
        getPostBySlug,
      }}
    >
      {children}
    </BlogContext.Provider>
  );
};
